import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

import type { GroupBy } from './buckets'

/** Horizontes del plan. Nada de rangos libres: tres opciones que se recuerdan. */
export const HORIZONS = [7, 14, 30] as const
export type Horizon = (typeof HORIZONS)[number]

export interface DueSoonFilters {
  horizon: Horizon
  groupBy: GroupBy
  company: string | null
  assignee: string | null
  /**
   * Qué tramo del tablero se mira.
   *
   * `vencidas` limita a lo que ya venció (el KPI rojo del portafolio) y
   * `por-vencer` es su complemento: lo que todavía está a tiempo. El segundo
   * existe porque el KPI "Vencen esta semana" prometía un número —los próximos
   * siete días— y aterrizaba en una pantalla que además listaba las quince
   * vencidas de antes. El número del tile y lo que se ve al tocarlo tienen que
   * ser la misma cosa.
   */
  state: 'vencidas' | 'por-vencer' | null
}

export const PARAM = {
  horizon: 'horizonte',
  groupBy: 'agrupar',
  company: 'empresa',
  assignee: 'responsable',
  state: 'estado',
} as const

export interface DueSoonDefaults {
  groupBy?: GroupBy
  horizon?: Horizon
}

export function parseDueSoonFilters(
  params: URLSearchParams,
  defaults: DueSoonDefaults = {},
): DueSoonFilters {
  const horizon = Number(params.get(PARAM.horizon))
  const groupBy = params.get(PARAM.groupBy)

  return {
    horizon: (HORIZONS as readonly number[]).includes(horizon)
      ? (horizon as Horizon)
      : (defaults.horizon ?? 14),
    groupBy:
      groupBy === 'proyecto' || groupBy === 'persona' ? groupBy : (defaults.groupBy ?? 'persona'),
    company: params.get(PARAM.company) || null,
    assignee: params.get(PARAM.assignee) || null,
    state: ((valor) => (valor === 'vencidas' || valor === 'por-vencer' ? valor : null))(
      params.get(PARAM.state),
    ),
  }
}

export function toQueryFilters(filters: DueSoonFilters): Record<string, string> {
  const query: Record<string, string> = { [PARAM.horizon]: String(filters.horizon) }
  if (filters.company) query[PARAM.company] = filters.company
  if (filters.assignee) query[PARAM.assignee] = filters.assignee
  if (filters.state) query[PARAM.state] = filters.state
  return query
}

export interface UseDueSoonFilters {
  filters: DueSoonFilters
  queryFilters: Record<string, string>
  isFiltered: boolean
  setFilter: <K extends keyof DueSoonFilters>(key: K, value: DueSoonFilters[K]) => void
  clearFilters: () => void
}

/** Igual que en el portafolio: la URL es la fuente de verdad, para compartir. */
export function useDueSoonFilters(defaults: DueSoonDefaults = {}): UseDueSoonFilters {
  const [params, setParams] = useSearchParams()
  const groupByDefault = defaults.groupBy
  const horizonDefault = defaults.horizon
  const filters = useMemo(
    () => parseDueSoonFilters(params, { groupBy: groupByDefault, horizon: horizonDefault }),
    [params, groupByDefault, horizonDefault],
  )

  const setFilter = useCallback<UseDueSoonFilters['setFilter']>(
    (key, value) => {
      setParams(
        (current) => {
          const next = new URLSearchParams(current)
          const name = PARAM[key]
          if (value === null || value === '') next.delete(name)
          else next.set(name, String(value))
          return next
        },
        { replace: true },
      )
    },
    [setParams],
  )

  const clearFilters = useCallback(() => {
    setParams(
      (current) => {
        const next = new URLSearchParams(current)
        next.delete(PARAM.company)
        next.delete(PARAM.assignee)
        next.delete(PARAM.state)
        return next
      },
      { replace: true },
    )
  }, [setParams])

  return {
    filters,
    queryFilters: useMemo(() => toQueryFilters(filters), [filters]),
    isFiltered: Boolean(filters.company || filters.assignee || filters.state),
    setFilter,
    clearFilters,
  }
}
